export interface ProjectStats {
  features?: number
  labels?: number
  skipped_arch?: number
  /** Metryczka paczki QGIS; brak pola = paczki nie ma albo wydawanie
   *  paczek jest wylaczone (QGIS_EXPORT). Steruje przyciskami pobierania. */
  qgis_export?: { layers: number; features: number; symbols: number; bytes: number }
  /** Ile trwalo wczytanie, w sekundach: GML do bazy, symbolika, calosc.
   *  Projekty wczytane przed wprowadzeniem pomiaru tych pol nie maja. */
  import_s?: number
  bake_s?: number
  total_s?: number
  [k: string]: unknown
}

export interface Project {
  id: string
  name: string
  source_file: string
  size_bytes: number
  status: 'pending' | 'parsing' | 'importing' | 'styling' | 'ready' | 'error'
  stage: string | null
  progress: number
  error: string | null
  srid: number | null
  gml_version: string | null
  stats: ProjectStats
  created_at: string | null
  /** Kiedy projekt zniknie, o ile nikt go wcześniej nie otworzy. */
  expires_at: string | null
  extent?: [number, number, number, number] | null
  layers?: LayerInfo[]
}

export interface LayerInfo {
  name: string
  geom_type: string
  feature_count: number
  /** Grupa z drzewa warstw wtyczki (Standardowa legenda). */
  group: string
  /** Z-rank: im wiekszy, tym wyzej na mapie. Lista przychodzi juz posortowana. */
  rank: number
}

/** Klasa renderera z podgladem symbolu - to, co QGIS pokazuje w drzewie warstw. */
export interface LegendItem { label: string; icon: string }
export type Legend = Record<string, LegendItem[]>

/** Instrukcja rysowania - dokladnie to, co wyplula symbolika QGIS. */
export type Op =
  | { op: 'line'; g: number[][][]; c: string; w: number; d: number[] | null }
  | { op: 'fill'; g: number[][][][]; f: string | null; c: string | null; w: number }
  | { op: 'marker'; g: number[][]; shape: string; f: string; c: string; w: number; s: number; rot: number }
  | { op: 'svg'; g: number[][]; u: string; s: number; rot: number }   // u = klucz w RenderResult.symbols

export interface RenderFeature { id: number; ops: Op[]; bb?: [number, number, number, number] }
export interface RenderLayer { name: string; features: RenderFeature[] }

export interface Label {
  l: string; t: string; x: number; y: number
  rot: number; fs: number; halo: number; q: number
  /** kolor napisu ze stylu; bywa data-defined (np. barwa branzy dla rzednych GESUT) */
  c: string
  /** kolor otoczki, null gdy styl jej nie zada */
  hc: string | null
}

export interface RenderResult {
  band: number
  srid: number
  truncated: boolean
  /** klucz symbolu -> data URI; instrukcje svg niosa sam klucz */
  symbols: Record<string, string>
  layers: RenderLayer[]
  labels: Label[]
}

export interface FeatureDetail {
  id: number
  layer: string
  gml_id: string | null
  /** Ludzki identyfikator obiektu (idDzialki, idBudynku...), gdy klasa go ma. */
  title: string | null
  attrs: { k: string; v: string }[]
}

/** Wiersz tabeli atrybutow. */
export interface TableRow {
  id: number
  gml_id: string | null
  /** Obwiednia obiektu - do przyblizenia z wiersza; null dla obiektu bez geometrii. */
  ext: [number, number, number, number] | null
  /** Wartosci ulozone wedlug TablePage.fields - bez powtarzania nazw kolumn. */
  values: (string | null)[]
}

export interface TablePage {
  layer: string
  fields: string[]
  total: number
  offset: number
  rows: TableRow[]
}

/** Zestawienie EGiB dostępne dla projektu. */
export interface ReportInfo {
  key: string
  title: string
  base: string
  /** czy projekt ma warstwę, ktorej obiekt jest wierszem */
  ready: boolean
  /** warstwy, bez ktorych zestawienie bedzie ubozsze */
  missing: string[]
  note: string
  /** Wiersz otwiera osobny dokument, a nie jest calym wynikiem. */
  doc: boolean
}

/** Strona zestawienia - ten sam ksztalt wierszy co tabela atrybutow. */
export interface ReportPage {
  key: string
  title: string
  columns: string[]
  total: number
  offset: number
  rows: TableRow[]
  missing: string[]
}

async function json<T>(res: Response): Promise<T> {
  if (!res.ok) throw new Error(`${res.status} ${await res.text()}`)
  return res.json() as Promise<T>
}

export const api = {
  /**
   * Plik GML albo archiwum ZIP z jednym GML-em w środku.
   *
   * XMLHttpRequest, a nie fetch: plik powiatowy ma pół gigabajta i jego
   * wysyłanie trwa, a bez postępu panel wygląda przez ten czas na zawieszony.
   * fetch postępu wysyłania nie raportuje (strumień w żądaniu to wciąż jedna
   * przeglądarka i wymóg HTTP/2), XHR robi to jednym zdarzeniem.
   */
  upload(file: File, importArch: boolean,
         onProgress?: (wyslane: number, calosc: number) => void): Promise<{ id: string }> {
    const fd = new FormData()
    fd.append('file', file)
    fd.append('import_arch', String(importArch))

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest()
      xhr.open('POST', '/api/projects')
      xhr.upload.onprogress = e => {
        if (e.lengthComputable) onProgress?.(e.loaded, e.total)
      }
      // Ten sam ksztalt bledu co przy fetch ("400 {...}"), bo czytelnyBlad()
      // wyluskuje z niego komunikat serwera.
      xhr.onload = () => {
        if (xhr.status < 200 || xhr.status >= 300) {
          reject(new Error(`${xhr.status} ${xhr.responseText}`))
          return
        }
        try { resolve(JSON.parse(xhr.responseText)) }
        catch { reject(new Error(`${xhr.status} ${xhr.responseText}`)) }
      }
      xhr.onerror = () => reject(new Error('połączenie z serwerem przerwane'))
      xhr.onabort = () => reject(new Error('wysyłanie przerwane'))
      xhr.send(fd)
    })
  },

  /** Paczka z projektem QGIS - zwykly odnosnik, plik idzie strumieniem z API. */
  exportUrl(id: string): string {
    return `/api/projects/${id}/export`
  },

  async list(): Promise<Project[]> {
    return json(await fetch('/api/projects'))
  },

  async get(id: string): Promise<Project> {
    return json(await fetch(`/api/projects/${id}`))
  },

  async legend(id: string): Promise<Legend> {
    return json(await fetch(`/api/projects/${id}/legend`))
  },

  async remove(id: string): Promise<void> {
    const res = await fetch(`/api/projects/${id}`, { method: 'DELETE' })
    // Bez sprawdzenia nieudane kasowanie wygladalo w panelu na udane: karta
    // znikala i wracala dopiero przy najblizszym odswiezeniu listy.
    if (!res.ok) throw new Error(`${res.status} ${await res.text()}`)
  },

  async render(id: string, bbox: number[], scale: number, layers?: string[],
               signal?: AbortSignal): Promise<RenderResult> {
    const p = new URLSearchParams({ bbox: bbox.join(','), scale: String(Math.round(scale)) })
    if (layers?.length) p.set('layers', layers.join(','))
    return json(await fetch(`/api/projects/${id}/render?${p}`, { signal }))
  },

  async feature(id: string, featureId: number): Promise<FeatureDetail> {
    return json(await fetch(`/api/projects/${id}/features/${featureId}`))
  },

  /** Strona tabeli atrybutow warstwy; bbox ogranicza ja do widoku mapy. */
  async rows(id: string, layer: string,
             opts: { limit?: number; offset?: number; bbox?: number[] } = {},
             signal?: AbortSignal): Promise<TablePage> {
    const p = new URLSearchParams()
    if (opts.limit) p.set('limit', String(opts.limit))
    if (opts.offset) p.set('offset', String(opts.offset))
    if (opts.bbox) p.set('bbox', opts.bbox.map(v => v.toFixed(3)).join(','))
    return json(await fetch(
      `/api/projects/${id}/layers/${encodeURIComponent(layer)}/rows?${p}`, { signal }))
  },

  /** Zestawienia, ktore ma sens uruchamiac dla tego projektu. */
  async reports(id: string): Promise<ReportInfo[]> {
    return json(await fetch(`/api/projects/${id}/reports`))
  },

  async report(id: string, key: string,
               opts: { limit?: number; offset?: number; bbox?: number[] } = {},
               signal?: AbortSignal): Promise<ReportPage> {
    const p = new URLSearchParams()
    if (opts.limit) p.set('limit', String(opts.limit))
    if (opts.offset) p.set('offset', String(opts.offset))
    if (opts.bbox) p.set('bbox', opts.bbox.map(v => v.toFixed(3)).join(','))
    return json(await fetch(`/api/projects/${id}/reports/${key}?${p}`, { signal }))
  },

  /** Wypis z rejestru gruntow dla jednej dzialki - ten sam adres do podgladu
      i do pobrania, rozni je tylko parametr. */
  docUrl(id: string, featureId: number, download = false): string {
    return `/api/projects/${id}/reports/rejestr/${featureId}${download ? '?download=1' : ''}`
  },

  /** Odnosniki do plikow CSV - pobieranie idzie zwyklym <a download>. */
  csvUrl(id: string, src: { kind: 'layer' | 'report'; key: string }, bbox?: number[]): string {
    const p = new URLSearchParams()
    if (bbox) p.set('bbox', bbox.map(v => v.toFixed(3)).join(','))
    const q = p.toString() ? `?${p}` : ''
    // Wypis z rejestru gruntow nie jest tabela, wiec calosc pobiera sie jako
    // jeden dokument HTML - tak samo, jak sklada go wtyczka.
    if (src.kind === 'report' && src.key === 'rejestr') {
      p.set('download', '1')
      return `/api/projects/${id}/reports/rejestr/all?${p}`
    }
    return src.kind === 'report'
      ? `/api/projects/${id}/reports/${src.key}/csv${q}`
      : `/api/projects/${id}/layers/${encodeURIComponent(src.key)}/rows.csv${q}`
  },

  /** Caly stos trafiony jednym klknieciem - jedno zapytanie zamiast N. */
  async features(id: string, ids: number[], signal?: AbortSignal): Promise<FeatureDetail[]> {
    const p = new URLSearchParams({ ids: ids.join(',') })
    return json(await fetch(`/api/projects/${id}/features?${p}`, { signal }))
  },
}
